import { createSelector } from 'reselect'
import { getSelectedFacets } from 'selectors/algolia2/data'
import { getSort, getSearch, getPage } from 'selectors/algolia2/url'
import { hasPrintsSelected } from './facets'
// FacetName => DataLayer Value
// Ex: materials => `material`, prints_materials => `prints materials`
const FACET_OVERRIDES = {
  artist_recognition: 'artist recognition',
  // original || print
  category: 'category',
  // original || print
  color: 'color',
  // original || print
  country: 'country',
  // original || print
  height: 'height range',
  // original
  materials: 'material',
  // original
  mediums: 'medium',
  // original
  orientation: 'orientation',
  // original || print
  price: 'price',
  // original
  prints_availability: 'print editions',
  // print
  prints_materials: 'prints material',
  // print
  prints_price_ranges: 'prints price range',
  // print
  prints_size_ranges: 'prints size range',
  // print
  size: 'size',
  // original
  styles: 'style',
  // original || print
  subject: 'subject',
  // original || print
  toaf_cities: 'toaf cities',
  // original || print
  width: 'width range', // original
}
// Pass these FACETS into DATALAYER for ORIGINAL Browse
const ORIGINAL_FACETS = [
  'category',
  'color',
  'country',
  'height', // custom size inputs
  'materials',
  'mediums',
  'orientation',
  'price',
  'size',
  'styles',
  'subject',
  'width', // custom size inputs
]
// Pass these FACETS into DATALAYER for PRINTS Browse
const PRINT_FACETS = [
  'category',
  'color',
  'country',
  'orientation',
  'prints_availability',
  'prints_materials',
  'prints_price_ranges',
  'prints_size_ranges',
  'styles',
  'subject',
]
const DATALAYER_DEFAULTS = {
  event: 'pagetype',
  pagetype: 'browse',
  pagination: 1,
  sort: 'default',
  subpagetype: '',
}
export const getDataLayer = createSelector(
  hasPrintsSelected,
  getSelectedFacets,
  getPage, // current page
  getSort,
  getSearch,
  (hasPrints, selectedFacets, page, sort, query): Record<string, any> => {
    // Starting Point/Fields we NEED
    const data: Record<string, any> = { ...DATALAYER_DEFAULTS }
    // Which Browse Facet to use
    let attributes = ORIGINAL_FACETS

    if (hasPrints) {
      attributes = PRINT_FACETS
    }

    // Lets GRAB all current selected facets
    attributes.forEach((facetName) => {
      const facetAttributes = selectedFacets[facetName] || []
      const layerName = FACET_OVERRIDES[facetName] || facetName
      data[layerName] = 'all'

      if (!facetAttributes.length) {
        return
      }

      if (Array.isArray(facetAttributes)) {
        data[layerName] = facetAttributes.join(',')
        return
      }

      data[layerName] = facetAttributes
    })
    // Lets start updating
    data.subpagetype = hasPrints ? 'prints' : 'originals'
    data.pagination = page || 1
    data.sort = sort || 'default'

    // data.subpagetype = (category || style || subject || mediums || materials || '').toLowerCase()
    // Search SubType
    if (query) {
      data.subpagetype = 'search'
    }

    // Toaf Badges
    data.badges = []

    if (Array.isArray(selectedFacets.artist_recognition)) {
      selectedFacets.artist_recognition.forEach((badge) => {
        if (badge !== 'the other art fair') {
          data.badges.push(badge)
        }
      })
    }

    if (Array.isArray(selectedFacets.toaf_cities)) {
      selectedFacets.toaf_cities.forEach((city) => data.badges.push(`toaf ${city}`))
    }

    data.badges.join(',')

    if (!data.badges.length) {
      delete data.badges
    }

    // Original Custom Inputs
    if ((selectedFacets.width || []).length || (selectedFacets.height || []).length) {
      delete data.size
    } else {
      delete data['width range']
      delete data['height range']
    }

    // Complete Data Layer
    return data
  }
)